//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DataTableConfig } from '@tmt-insights/oms_fe_core';
import { DeepPartial, IngestFile } from '@/redux/slices/slice_types';
import { defaultFilters, NewFilterTypesMap, FilterIDType, IngestManagementGridItemRender } from '@tmt-insights/oms_fe_core/columns';

// baseUrl = the base url for the table that `/search` is appended to
// title = the title of the table that appears beneath the header/navbar
// label = the label used when referring to items in the table
// filters = filters that appear above the table
//           default options: status, managedBy, createdAt, filterId, search, jobType, stage,
//                            dueDate, completedAt, linearTxDate, qcByDate, qcOnPrem, customer,
//                            profile, assignedUsers, showResolved, showNotReady, filterIdNoJobAsset,
//                            fileType, entityType, showNotReady, showResolved
// preset = the default params to apply when the table is loaded that can be replaced by filters, such as sorting/ordering
// params = params to apply to the table when it is loaded that are applied to every request, cannot be replaced by filters
// disableExport = disable the export button when filters are applied
// parseDataFn = function to parse the data from the response before it is displayed in the table,
//               and should return the array of items to display in the table, eg. (data: any) => data?.response?.items

const dataConfig: DeepPartial<DataTableConfig> = {
  orders: {
    filters: [
      defaultFilters.status,
      defaultFilters.managedBy,
      defaultFilters.createdAt,
      defaultFilters.completedAt,
      // defaultFilters.nonBillableCustomer,
      defaultFilters.customer,
      {
        id: 'po_filter',
        label: 'PO',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'cost_centre_filter',
        label: 'Cost Centre',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'opportunity_id_filter',
        label: 'Opportunity ID',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'assigned_to_vendor_filter',
        label: 'Assigned To Vendor',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'source_id_filter',
        label: 'Source ID',
        type: NewFilterTypesMap.TEXT
      },
      defaultFilters.profile,
      defaultFilters.filterId,
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.status,
      defaultFilters.managedBy,
      defaultFilters.createdAt,
      defaultFilters.completedAt,
      defaultFilters.customer,
      {
        id: 'po_filter',
        label: 'PO',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'cost_centre_filter',
        label: 'Cost Centre',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'opportunity_id_filter',
        label: 'Opportunity ID',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'assigned_to_vendor_filter',
        label: 'Assigned To Vendor',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'source_id_filter',
        label: 'Source ID',
        type: NewFilterTypesMap.TEXT
      },
      defaultFilters.profile,
      defaultFilters.filterId,
      defaultFilters.search
    ]
  },
  jobs: {
    filters: [
      defaultFilters.jobType,
      defaultFilters.stage,
      defaultFilters.status,
      {
        id: 'asset_confirmation_filter',
        label: 'Asset Confirmation',
        type: NewFilterTypesMap.NULLABLE_BOOLEAN
      },
      defaultFilters.dueDate,
      defaultFilters.completedAt,
      defaultFilters.createdAt,
      defaultFilters.managedBy,
      defaultFilters.customer,
      {
        id: 'assigned_to_vendor_filter',
        label: 'Assigned To Vendor',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'source_id_filter',
        label: 'Source ID',
        type: NewFilterTypesMap.TEXT
      },
      defaultFilters.profile,
      defaultFilters.filterId,
      {
        id: 'filter_id_bbc',
        label: 'Search by BBCS ID',
        type: NewFilterTypesMap.ID,
        options: () => [FilterIDType.JOB_BUNDLE_ID, FilterIDType.VERSION_RLID, FilterIDType.VERSION_P7ID, FilterIDType.VERSION_MCID],
        filterBehavior: {
          filterInputType: 'text',
          noAdornment: true,
          defaultOption: FilterIDType.VERSION_RLID
        }
      },
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.jobType,
      defaultFilters.stage,
      defaultFilters.status,
      {
        id: 'asset_confirmation_filter',
        label: 'Asset Confirmation',
        type: NewFilterTypesMap.NULLABLE_BOOLEAN
      },
      defaultFilters.dueDate,
      defaultFilters.completedAt,
      defaultFilters.createdAt,
      defaultFilters.managedBy,
      defaultFilters.customer,
      {
        id: 'assigned_to_vendor_filter',
        label: 'Assigned To Vendor',
        type: NewFilterTypesMap.TEXT
      },
      {
        id: 'source_id_filter',
        label: 'Source ID',
        type: NewFilterTypesMap.TEXT
      },
      defaultFilters.profile,
      defaultFilters.filterId,
      {
        id: 'filter_id_bbc',
        label: 'Search by BBCS ID',
        type: NewFilterTypesMap.ID,
        options: () => [FilterIDType.JOB_BUNDLE_ID, FilterIDType.VERSION_RLID, FilterIDType.VERSION_P7ID, FilterIDType.VERSION_MCID],
        filterBehavior: {
          filterInputType: 'text',
          noAdornment: true,
          defaultOption: FilterIDType.VERSION_RLID
        }
      },
      defaultFilters.search
    ]
  },
  pendingAssets: {
    filters: [
      defaultFilters.status,
      defaultFilters.componentType,
      defaultFilters.assignedUsers,
      defaultFilters.assignedGroups,
      defaultFilters.dueDate,
      defaultFilters.filterId,
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.status,
      defaultFilters.componentType,
      defaultFilters.assignedUsers,
      defaultFilters.assignedGroups,
      defaultFilters.dueDate,
      defaultFilters.filterId,
      defaultFilters.search
    ]
  },
  profiles: {
    quickFilters: [defaultFilters.customerWithProfiles, defaultFilters.search],
    disableExport: true
  },
  processingTasks: {
    filters: [defaultFilters.showResolved, defaultFilters.dueDate, defaultFilters.createdAt, defaultFilters.filterId, defaultFilters.search],
    quickFilters: [defaultFilters.showResolved, defaultFilters.dueDate, defaultFilters.createdAt, defaultFilters.filterId, defaultFilters.search]
  },
  titlesV2: {
    title: 'Titles',
    filters: [
      defaultFilters.entityType,
      {
        id: 'filter_id_bbc',
        label: 'Search by BBCS ID',
        type: NewFilterTypesMap.ID,
        options: () => [FilterIDType.JOB_BUNDLE_ID, FilterIDType.RLID, FilterIDType.P7ID, FilterIDType.MCID],
        filterBehavior: {
          filterInputType: 'text',
          noAdornment: true,
          defaultOption: FilterIDType.RLID
        }
      },
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.entityType,
      {
        id: 'filter_id_bbc',
        label: 'Search by BBCS ID',
        type: NewFilterTypesMap.ID,
        options: () => [FilterIDType.JOB_BUNDLE_ID, FilterIDType.RLID, FilterIDType.P7ID, FilterIDType.MCID],
        filterBehavior: {
          filterInputType: 'text',
          noAdornment: true,
          defaultOption: FilterIDType.RLID
        }
      },
      defaultFilters.search
    ],
    params: {
      entity_types_filter: ['feature', 'series', 'episode', 'season'],
      thumbnails: true
    }
  },
  tasks: {
    params: {
      name_filter: [],
      task_type_filter: ['QC']
    },
    filters: [
      defaultFilters.taskType,
      defaultFilters.status,
      defaultFilters.assignedUsers,
      defaultFilters.dueDate,
      defaultFilters.createdAt,
      defaultFilters.showNotReady,
      defaultFilters.filterIdNoJobAsset,
      defaultFilters.customer,
      defaultFilters.search
    ],
    quickFilters: [
      defaultFilters.taskType,
      defaultFilters.status,
      defaultFilters.assignedUsers,
      defaultFilters.dueDate,
      defaultFilters.createdAt,
      defaultFilters.showNotReady,
      defaultFilters.filterIdNoJobAsset,
      defaultFilters.customer,
      defaultFilters.search
    ]
  },
  fileIngest: {
    baseUrl: '/ingest_manager/ingest_records/',
    filters: [defaultFilters.fileType, defaultFilters.stage, defaultFilters.status, defaultFilters.createdAt, defaultFilters.search],
    quickFilters: [defaultFilters.fileType, defaultFilters.stage, defaultFilters.status, defaultFilters.createdAt, defaultFilters.search],
    enableGridViewToggle: true,
    disablePreview: true,
    gridItemRender: (row: IngestFile, _key: string, onGridClick: (id: number) => void) => {
      return <IngestManagementGridItemRender key={`row-${row.id}`} data={row} onGridClick={() => onGridClick(row.id)} />;
    }
  },
  jobDetailTasks: {
    disableGetData: true,
    assetConfirmationFilterEnabled: true
  }
};

export default dataConfig;
